import React, { useCallback, useEffect, useMemo, useState } from 'react';
import emptyDishImage from '../../static/images/empty-dish.png';
import emptyMenuImage from '../../static/images/empty-menu.png';
import {
    DragDropContext,
    Draggable,
    Droppable,
    type DraggableProvided,
    type DraggableStateSnapshot,
    type DroppableProvided,
    DropResult,
} from 'react-beautiful-dnd';

/** `react-beautiful-dnd` 与 React 18 类型声明不兼容，经 unknown 断言为 FC 供 JSX 使用 */
const MenuLibraryDndContext = DragDropContext as unknown as React.FC<{
    onDragEnd: (result: DropResult) => void;
    children: React.ReactNode;
}>;
const MenuLibraryDroppable = Droppable as unknown as React.FC<{
    droppableId: string;
    children: (provided: DroppableProvided) => React.ReactNode;
}>;
const MenuLibraryDraggable = Draggable as unknown as React.FC<{
    draggableId: string;
    index: number;
    isDragDisabled?: boolean;
    children: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => React.ReactNode;
}>;
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Menu,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import i18n from '../../i18n';
import CommonInput from '../../component/form/common-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import {
    MenuLibraryManagementState,
    MENU_LIBRARY_WEEK_HOLIDAY_NUM,
    MENU_LIBRARY_WEEK_VALUES,
    getMenuLibraryDetailDishRowKey,
    getMenuLibraryDetailSecondSluKey,
    isMenuLibraryDetailDishRowSelectable,
    canDeleteEmptyMenu,
    canDeleteEmptySubcategory,
} from './menu-library-management-state';
import { MenuLanguageMenuButton } from './menu-library-language-menu-button.component';
import { MenuDish, type SecondMenu } from '../../models';
import {
    VirtualizedMuiTable,
    type VirtualizedMuiTableColumn,
    type VirtualizedMuiTableRowReorder,
} from '../../component/table/virtualized-mui-table.component';
import ShareMenuDialog from '../../component/share-menu-dialog/share-menu-dialog.component';
import { MenuLibraryShareStoreTargetDialog } from './menu-library-share-store-target-dialog.component';
import { MenuLibraryShareSuccessDialog } from './menu-library-share-success-dialog.component';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import { accountState } from '../my-account/account-state';
import EditOffRoundedIcon from '@mui/icons-material/EditOffRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
/** Tabler chevron-down，旋转后作为向右箭头（顶栏右侧返回） */
import { ReactComponent as TablerChevronDownIcon } from '../../static/images/svg/tabler_chevron-down.svg';
/** Tabler Icons：edit（tabler:edit），资源为 static/images/svg/tabler_edit.svg */
import { ReactComponent as EditIcon } from '../../static/images/svg/tabler_edit.svg';
import { ReactComponent as ShareIcon } from '../../static/images/svg/octicon_share-16.svg';
/** Figma 9758:39025：底部「加载云端菜品」 */
import { ReactComponent as CloudDownloadIcon } from '../../static/images/svg/iconamoon_cloud-download-fill.svg';
/** Figma 9752:39005：底部「新增菜品」加号 */
import { ReactComponent as AddRoundedIcon } from '../../static/images/svg/ic_round-plus.svg';
import { ReactComponent as FilterIcon } from '../../static/images/svg/filter.svg';
import { ReactComponent as SearchIcon } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as MenuLibraryMenuNameIcon } from '../../static/images/svg/menu_library_detail_menu_name_icon.svg';
/** Figma 3114:45266 成人档头像图标（与儿童/其他药丸并列） */
import { ReactComponent as MenuLibraryPriceAdultFaceIcon } from '../../static/images/svg/menu_library_price_adult_face.svg';
import { ReactComponent as ChildCareIcon } from '../../static/images/svg/child_care.svg';
import { ReactComponent as FaceSmileIcon } from '../../static/images/svg/mdi_face-outline.svg';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import { ReactComponent as ArrowDropdownIcon } from '../../static/images/svg/iconamoon_arrow-up-2-fill.svg';
import { MenuLibraryDishFilterDialog } from './menu-library-dish-filter-dialog.component';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { MenuLibraryDishSearchDialog } from './menu-library-dish-search-dialog.component';
import { MenuLibraryAddSubcategoryDialog } from './menu-library-add-subcategory-dialog.component';
import {
    MenuLibrarySubcategoryPickerDialog,
    type MenuLibrarySubcategoryPickerMode,
} from './menu-library-subcategory-picker-dialog.component';
import {
    MenuLibraryCloudSubcategoryPickerDialog,
    type CloudSubcategoryPickerTarget,
} from './menu-library-cloud-subcategory-picker-dialog.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { getMainGroupNameForDisplay } from '../../utils/menu-main-group-name-i18n';
/** 与新建子分类详情底部批量条共用样式（`.menu-library-create-dialog__dish-batch-*`） */
import './scss/menu-library-create-menu-dialog.style.scss';
import './scss/menu-library-menu-detail.style.scss';
/** solar:copy-bold（批量复制菜品） */
import { ReactComponent as CopyBoldIcon } from '../../static/images/svg/solar_copy-bold.svg';
/** ic:baseline-delete-forever（批量删除菜品） */
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
/** gg:arrows-exchange（批量转移菜品） */
import { ReactComponent as GgArrowsExchangeIcon } from '../../static/images/svg/gg_arrows-exchange.svg';

/** 绑定费用药丸编辑：与下拉档位对应（成人/儿童/其他） */
type PriceEditFieldKey = 'adult' | 'child' | 'other';

/** 药丸档位 → FirstMenu extra 字段 */
const PRICE_KEY_TO_EXTRA: Record<PriceEditFieldKey, 'extra_normal' | 'extra_child' | 'extra_other'> = {
    adult: 'extra_normal',
    child: 'extra_child',
    other: 'extra_other',
};

/** 星期值到 i18n key（与 business_hour 一致；节假日 week=7） */
function weekValueToLabelKey(week: number): string {
    if (week === MENU_LIBRARY_WEEK_HOLIDAY_NUM) {
        return 'menu_library_create_dialog_week_holiday';
    }
    const map: Record<number, string> = {
        0: 'business_hour_page_Sunday',
        1: 'business_hour_page_Monday',
        2: 'business_hour_page_Tuesday',
        3: 'business_hour_page_Wednesday',
        4: 'business_hour_page_Thursday',
        5: 'business_hour_page_Friday',
        6: 'business_hour_page_Saturday',
    };
    return map[week] ?? 'business_hour_page_Monday';
}

type Props = {
    /** 菜单库管理状态（含详情数据与操作） */
    state: MenuLibraryManagementState;
    /** 顶栏「星期」药丸下拉宽度（传给 CommonInput / PrefixLabeledPillSelect）；默认 100px */
    weekPillSelectWidth?: string | number;
};

/** `loadDetailData` 期间占位，布局与详情顶栏 + 左子类 + 右表一致 */
function MenuLibraryMenuDetailSkeleton(props: { onBack: () => void }) {
    const { onBack } = props;
    const bar = (w: number, h: number) => (
        <Skeleton variant="rounded" animation="wave" width={w} height={h} sx={{ borderRadius: '8px', flexShrink: 0 }} />
    );
    return (
        <Box
            className="menu-library-menu-detail menu-library-menu-detail--skeleton"
            aria-busy="true"
            aria-label={i18n.t('general_text_loading')}
        >
            <Paper elevation={0} className="menu-library-menu-detail__header-card">
                <Box className="menu-library-menu-detail__header-grid">
                    <Box className="menu-library-menu-detail__thumb-cell">
                        {/** 与真实 `thumb-fill` 一致：绝对铺满格，避免 in-flow 高度与右侧两行网格叠层 */}
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                display: 'block',
                                borderRadius: '8px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </Box>
                    <Box className="menu-library-menu-detail__header-row1">
                        <Stack direction="row" flexWrap="wrap" spacing={1} flex={1} minWidth={0} alignItems="center">
                            {bar(220, 40)}
                            {bar(100, 40)}
                            {bar(100, 40)}
                            {bar(48, 48)}
                            {bar(48, 48)}
                        </Stack>
                        <Tooltip title={i18n.t('menu_library_detail_back')}>
                            <IconButton
                                size="large"
                                onClick={onBack}
                                aria-label={i18n.t('menu_library_detail_back')}
                                className="menu-library-menu-detail__header-nav-right"
                            >
                                <TablerChevronDownIcon className="menu-library-menu-detail__header-chevron-right" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box className="menu-library-menu-detail__header-row2">
                        {bar(100, 48)}
                        {bar(140, 56)}
                        {bar(140, 56)}
                        {bar(140, 56)}
                        <Skeleton variant="rounded" animation="wave" width={200} height={32} sx={{ borderRadius: '8px' }} />
                        {bar(48, 48)}
                        {bar(48, 48)}
                    </Box>
                </Box>
            </Paper>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className="menu-library-menu-detail__body">
                <Paper elevation={0} className="menu-library-menu-detail__left">
                    <Stack direction="row" spacing={3} alignItems="center" className="menu-library-menu-detail__left-title">
                        <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rounded" width={140} height={26} sx={{ borderRadius: '4px' }} />
                    </Stack>
                    <Box className="menu-library-menu-detail__left-list-scroll">
                        <Stack spacing={1}>
                            {[0, 1, 2, 3, 4, 5].map((k) => (
                                <Skeleton key={k} variant="rounded" animation="wave" height={44} sx={{ borderRadius: '30px' }} />
                            ))}
                        </Stack>
                    </Box>
                </Paper>
                <Paper elevation={0} className="menu-library-menu-detail__right">
                    <Box className="menu-library-menu-detail__dish-table-stack menu-library-menu-detail__dish-table-stack--skeleton">
                        <Box className="menu-library-menu-detail__dish-table-host menu-library-menu-detail__skeleton-table-host">
                            <Stack spacing={1} sx={{ p: 2, pt: 2, flex: 1, minHeight: 0 }}>
                                <Skeleton variant="rounded" animation="wave" height={40} sx={{ borderRadius: '8px' }} />
                                {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
                                    <Skeleton key={k} variant="rounded" animation="wave" height={44} sx={{ borderRadius: '6px' }} />
                                ))}
                            </Stack>
                        </Box>
                        <Box className="menu-library-menu-detail__dish-add-row">
                            <Skeleton variant="rounded" animation="wave" height={48} sx={{ width: '100%', borderRadius: '8px' }} />
                        </Box>
                    </Box>
                </Paper>
            </Stack>
        </Box>
    );
}

/** 一级菜单名称编辑器的受控输入参数。 */
export type MenuLibraryMenuNameEditorProps = {
    /** 当前一级菜单名称草稿。 */
    value: string;
    /** 一级菜单名称草稿变更回调。 */
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * 组装删除确认弹窗第二行文案：菜单「名」或菜品小类「名」。
 * @param args.type 删除对象类型；关闭时为 null
 * @param args.menuName 当前一级菜单展示名（按详情展示语言）
 * @param args.subcategoryName 当前小类展示名（按详情展示语言）
 */
export function buildMenuLibraryDetailDeleteHighlightText(args: {
    /** 删除确认类型；关闭时为 null */
    type: 'menu' | 'subcategory' | null;
    /** 当前菜单展示名 */
    menuName: string;
    /** 当前小类展示名 */
    subcategoryName: string;
}): string {
    /** 空名兜底，避免书名号内空白 */
    const fallbackName = '—';
    if (args.type === 'menu') {
        return i18n.t('menu_library_detail_delete_menu_highlight', {
            name: args.menuName.trim() || fallbackName,
        });
    }
    if (args.type === 'subcategory') {
        return i18n.t('menu_library_detail_delete_subcategory_highlight', {
            name: args.subcategoryName.trim() || fallbackName,
        });
    }
    return '';
}

/**
 * 渲染详情顶栏的一级菜单名称编辑器。
 * 编辑态不显示额外提示，避免占用输入框宽度。
 */
export function MenuLibraryMenuNameEditor({ value, onChange }: Readonly<MenuLibraryMenuNameEditorProps>) {
    return (
        <Box className="menu-library-menu-detail__price-pill menu-library-menu-detail__menu-name-field menu-library-menu-detail__menu-name-field--editing">
            <MenuLibraryMenuNameIcon className="menu-library-menu-detail__title-pill-icon" aria-hidden />
            <CommonInput
                inputType="INPUT"
                className="menu-library-menu-detail__menu-name-input"
                value={value}
                onChange={onChange}
                size="small"
                placeholder={i18n.t('global_text_name')}
                InputLabelProps={{
                    shrink: true,
                    className: 'menu-library-menu-detail__week-label-hidden',
                }}
                label=""
            />
        </Box>
    );
}

/** 暂无子类别时的详情空状态参数。 */
export type MenuLibraryDetailEmptySubcategoryStateProps = {
    /** 是否允许删除当前空菜单。 */
    canDeleteMenu: boolean;
    /** 点击删除当前菜单按钮时的回调。 */
    onDeleteMenu: () => void;
    /** 点击新增子类别按钮时的回调。 */
    onAddSubcategory: () => void;
};

/**
 * 渲染菜单详情的暂无子类别状态。
 * 该状态只负责展示设计稿内容与触发操作，不直接依赖详情状态对象。
 */
export function MenuLibraryDetailEmptySubcategoryState({
    canDeleteMenu,
    onDeleteMenu,
    onAddSubcategory,
}: Readonly<MenuLibraryDetailEmptySubcategoryStateProps>) {
    return (
        <Box
            className="menu-library-menu-detail__empty-subcategory"
            aria-label={i18n.t('menu_library_detail_empty_subcategory_title')}
        >
            <Box
                component="img"
                className="menu-library-menu-detail__empty-subcategory-image"
                src={emptyMenuImage}
                alt={i18n.t('menu_library_detail_empty_subcategory_image_alt')}
            />
            <Typography variant="h2" className="menu-library-menu-detail__empty-subcategory-title">
                {i18n.t('menu_library_detail_empty_subcategory_title')}
            </Typography>
            <Typography
                variant="h3"
                className="menu-library-menu-detail__empty-subcategory-description"
            >
                {i18n.t('menu_library_detail_empty_subcategory_description')}
            </Typography>
            <Stack direction="row" spacing={0} className="menu-library-menu-detail__empty-subcategory-actions">
                {canDeleteMenu ? (
                    <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        className="menu-library-menu-detail__empty-subcategory-action menu-library-menu-detail__empty-subcategory-action--delete"
                        onClick={onDeleteMenu}
                        startIcon={
                            <BaselineDeleteForeverIcon
                                className="menu-library-menu-detail__empty-subcategory-delete-icon"
                                aria-hidden
                            />
                        }
                    >
                        <Typography component="span" variant="h3">
                            {i18n.t('menu_library_detail_delete_empty_menu')}
                        </Typography>
                    </Button>
                ) : null}
                <Button
                    type="button"
                    variant="outlined"
                    className="menu-library-menu-detail__empty-subcategory-action menu-library-menu-detail__empty-subcategory-action--add"
                    onClick={onAddSubcategory}
                    startIcon={
                        <AddRoundedIcon
                            className="menu-library-menu-detail__empty-subcategory-add-icon"
                            aria-hidden
                        />
                    }
                >
                    <Typography component="span" variant="h3">
                        {i18n.t('menu_library_detail_add_subcategory')}
                    </Typography>
                </Button>
            </Stack>
        </Box>
    );
}

/** 小类暂无菜品状态的展示参数。 */
export type MenuLibraryDetailEmptyDishStateProps = {
    /** 当前选中的小类名称。 */
    subcategoryName: string;
    /** 是否为云端菜单详情，用于选择对应的空菜品说明。 */
    isCloudMenuDetail: boolean;
    /** 是否允许删除当前空小类。 */
    canDeleteSubcategory: boolean;
    /** 本地详情是否显示加载云端菜品入口。 */
    showLoadCloudDish: boolean;
    /** 删除当前小类按钮回调。 */
    onDeleteSubcategory: () => void;
    /** 加载云端菜品按钮回调。 */
    onLoadCloudDish: () => void;
    /** 新增菜品按钮回调。 */
    onAddDish: () => void;
    /** 删除请求提交中时禁用删除按钮。 */
    deleteSubmitting?: boolean;
};

/**
 * 渲染小类暂无菜品状态。
 * 菜品操作沿用详情页既有回调，由父组件负责业务状态与弹窗。
 */
export function MenuLibraryDetailEmptyDishState({
    subcategoryName,
    isCloudMenuDetail,
    canDeleteSubcategory,
    showLoadCloudDish,
    onDeleteSubcategory,
    onLoadCloudDish,
    onAddDish,
    deleteSubmitting = false,
}: Readonly<MenuLibraryDetailEmptyDishStateProps>) {
    return (
        <Box className="menu-library-menu-detail__empty-dish">
            <Box
                component="img"
                className="menu-library-menu-detail__empty-dish-image"
                src={emptyDishImage}
                alt={i18n.t('menu_library_detail_empty_dish_image_alt')}
            />
            <Typography variant="h2" className="menu-library-menu-detail__empty-dish-title">
                {i18n.t('menu_library_detail_empty_dish_title')}
            </Typography>
            <Typography variant="h3" className="menu-library-menu-detail__empty-dish-description">
                {i18n.t(
                    isCloudMenuDetail
                        ? 'menu_library_detail_empty_dish_description_cloud'
                        : 'menu_library_detail_empty_dish_description_local',
                    {
                        subcategory: subcategoryName,
                    },
                )}
            </Typography>
            {canDeleteSubcategory ? (
                <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    className="menu-library-menu-detail__empty-dish-delete"
                    disabled={deleteSubmitting}
                    onClick={onDeleteSubcategory}
                    startIcon={
                        <BaselineDeleteForeverIcon
                            className="menu-library-menu-detail__empty-dish-delete-icon"
                            aria-hidden
                        />
                    }
                >
                    <Typography component="span" variant="h3">
                        {i18n.t('menu_library_detail_delete_empty_subcategory')}
                    </Typography>
                </Button>
            ) : null}
            <Box className="menu-library-menu-detail__empty-dish-actions">
                {showLoadCloudDish ? (
                    <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        className="menu-library-menu-detail__empty-dish-action menu-library-menu-detail__empty-dish-action--load-cloud"
                        onClick={onLoadCloudDish}
                        startIcon={
                            <CloudDownloadIcon
                                className="menu-library-menu-detail__empty-dish-action-icon"
                                aria-hidden
                            />
                        }
                    >
                        <Typography component="span" variant="h3">
                            {i18n.t('menu_library_load_cloud_open')}
                        </Typography>
                    </Button>
                ) : null}
                <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    className="menu-library-menu-detail__empty-dish-action menu-library-menu-detail__empty-dish-action--new-dish"
                    onClick={onAddDish}
                    startIcon={
                        <AddRoundedIcon
                            className="menu-library-menu-detail__empty-dish-action-icon"
                            aria-hidden
                        />
                    }
                >
                    <Typography component="span" variant="h3">
                        {i18n.t('menu_library_create_dialog_add_dish')}
                    </Typography>
                </Button>
            </Box>
        </Box>
    );
}

/**
 * 菜单库 — 单菜单详情编辑（Figma 351:6687）：顶栏 + 左子类 + 右菜品表
 */
const MenuLibraryMenuDetailComponent: React.FC<Props> = observer(({ state, weekPillSelectWidth = '100px' }) => {
    const mainPageData = MobxObjectsManager.get(MainPageState);
    /** 绑定费用：药丸 + Menu（下拉项来自额外费用接口数据） */
    const [priceEditMenuAnchor, setPriceEditMenuAnchor] = useState<HTMLElement | null>(null);
    const [priceEditMenuField, setPriceEditMenuField] = useState<PriceEditFieldKey | null>(null);
    /**
     * 分享菜单弹窗门店列表（同集团 `belonged_group`）：
     * - **本地**：不含当前门店（本地只能同步到别的门店）
     * - **云端**：当前门店置顶，文案为「当前门店-{昵称}」（云端可 import 到本店本地）
     */
    const shareStoreOptions = useMemo(() => {
        const currentGroupId = state.menuLibraryRestaurant?.belonged_group?.trim();
        /** 菜单库模块当前门店 string_id */
        const currentStoreId = (state.menuLibraryRestaurant?.string_id ?? '').trim();
        /** 是否云端详情分享（可同步到当前门店本地） */
        const isCloudShare = state.isCloudMenuDetail;
        const list = mainPageData.belongedRestaurant ?? [];
        const sameGroup = (r: (typeof list)[number]) => {
            if (!currentGroupId) {
                return false;
            }
            return (r.belonged_group ?? '').trim() === currentGroupId;
        };
        /** 同集团门店选项（原始展示名） */
        const options = list
            .filter(sameGroup)
            .map((r) => ({
                value: r.string_id || '',
                label: (r.nickname || r.string_id || '').trim() || '—',
            }))
            .filter((o) => o.value !== '');

        if (!currentStoreId) {
            return options;
        }

        if (!isCloudShare) {
            return options.filter((o) => o.value !== currentStoreId);
        }

        /** 当前门店原始展示名（昵称或 string_id） */
        const currentName =
            (state.menuLibraryRestaurant?.nickname || currentStoreId).trim() || '—';
        /** 列表中是否已有当前门店 */
        const currentInList = options.find((o) => o.value === currentStoreId);
        /** 其余门店（不含当前） */
        const others = options.filter((o) => o.value !== currentStoreId);
        /** 置顶项：当前门店-XXXX */
        const pinnedCurrent = {
            value: currentStoreId,
            label: i18n.t('menu_library_share_current_store_label', {
                name: currentInList?.label || currentName,
            }),
        };
        return [pinnedCurrent, ...others];
    }, [
        mainPageData.belongedRestaurant,
        state.isCloudMenuDetail,
        state.menuLibraryRestaurant?.belonged_group,
        state.menuLibraryRestaurant?.nickname,
        state.menuLibraryRestaurant?.string_id,
    ]);

    useEffect(() => {
        if (!state.isDetailHeaderEditMode) {
            setPriceEditMenuAnchor(null);
            setPriceEditMenuField(null);
        }
    }, [state.isDetailHeaderEditMode]);

    /** 详情菜品表多选：批量操作（与新建子分类详情面板一致） */
    const [detailDishSelectedKeys, setDetailDishSelectedKeys] = useState<React.Key[]>([]);
    const [detailBatchPickerMode, setDetailBatchPickerMode] = useState<MenuLibrarySubcategoryPickerMode | null>(null);
    const [detailBatchPickerConfirmLoading, setDetailBatchPickerConfirmLoading] = useState(false);
    const [detailBatchDeleteConfirmOpen, setDetailBatchDeleteConfirmOpen] = useState(false);

    useEffect(() => {
        setDetailDishSelectedKeys([]);
    }, [state.detailMainGroupId, state.detailCloudMenuId, state.selectedSecondSluId, state.selectedCloudSluId]);

    const closeDetailBatchSubcategoryPicker = useCallback(() => {
        setDetailBatchPickerMode(null);
        setDetailBatchPickerConfirmLoading(false);
    }, []);

    const openDetailBatchSubcategoryPicker = useCallback(
        (mode: MenuLibrarySubcategoryPickerMode) => {
            if (state.isCloudMenuDetail) {
                // 与本地一致：先打开弹窗，后台刷新列表（含 menu_item_slu）；弹窗内用 loading 展示
                void state.fetchCloudMenuList();
                setDetailBatchPickerMode(mode);
            } else {
                void state.ensureCourseGroupPickerMenusLoaded();
                setDetailBatchPickerMode(mode);
            }
        },
        [state],
    );

    const handleDetailBatchSubcategoryPickerConfirm = useCallback(
        async (targetSecondMenu: SecondMenu) => {
            if (!detailBatchPickerMode || detailDishSelectedKeys.length === 0 || state.isCloudMenuDetail) {
                return;
            }
            const itemIds = detailDishSelectedKeys.filter((k): k is number => typeof k === 'number');
            if (itemIds.length === 0) {
                return;
            }
            setDetailBatchPickerConfirmLoading(true);
            try {
                const ok =
                    detailBatchPickerMode === 'transfer'
                        ? await state.batchTransferMenuLibraryDetailDishesByItemIds(itemIds, targetSecondMenu)
                        : await state.batchCopyMenuLibraryDetailDishesByItemIds(itemIds, targetSecondMenu);
                if (ok) {
                    closeDetailBatchSubcategoryPicker();
                    setDetailDishSelectedKeys([]);
                }
            } finally {
                setDetailBatchPickerConfirmLoading(false);
            }
        },
        [closeDetailBatchSubcategoryPicker, detailBatchPickerMode, detailDishSelectedKeys, state],
    );

    /** 云端详情：确认跨菜单目标小类后转移/复制 */
    const handleDetailCloudBatchSubcategoryPickerConfirm = useCallback(
        async (target: CloudSubcategoryPickerTarget) => {
            if (!detailBatchPickerMode || detailDishSelectedKeys.length === 0 || !state.isCloudMenuDetail) {
                return;
            }
            const cloudDishIds = detailDishSelectedKeys
                .map((k) => String(k))
                .filter((id) => id.trim() !== '' && !id.startsWith('cloud-dish-idx:'));
            if (cloudDishIds.length === 0) {
                return;
            }
            setDetailBatchPickerConfirmLoading(true);
            try {
                const ok =
                    detailBatchPickerMode === 'transfer'
                        ? await state.batchTransferMenuLibraryDetailCloudDishesByIds(cloudDishIds, target)
                        : await state.batchCopyMenuLibraryDetailCloudDishesByIds(cloudDishIds, target);
                if (ok) {
                    closeDetailBatchSubcategoryPicker();
                    setDetailDishSelectedKeys([]);
                }
            } finally {
                setDetailBatchPickerConfirmLoading(false);
            }
        },
        [closeDetailBatchSubcategoryPicker, detailBatchPickerMode, detailDishSelectedKeys, state],
    );

    const handleDetailBatchDelete = useCallback(async () => {
        if (state.isCloudMenuDetail) {
            const cloudDishIds = detailDishSelectedKeys
                .map((k) => String(k))
                .filter((id) => id.trim() !== '' && !id.startsWith('cloud-dish-idx:'));
            if (cloudDishIds.length === 0) {
                return;
            }
            const ok = await state.deleteMenuLibraryDetailCloudDishesByIds(cloudDishIds);
            if (ok) {
                setDetailDishSelectedKeys([]);
            }
            return;
        }
        const itemIds = detailDishSelectedKeys.filter((k): k is number => typeof k === 'number');
        if (itemIds.length === 0) {
            return;
        }
        await state.deleteMenuLibraryDetailDishesByItemIds(itemIds);
        setDetailDishSelectedKeys([]);
    }, [detailDishSelectedKeys, state]);

    /** 子分类拖拽落位：调用 state 内 `PATCH menus/second/index` */
    const onDetailSecondSluDragEnd = useCallback(
        (result: DropResult) => {
            if (!state.isDetailSecondMenuPrioritySortEnabled) {
                return;
            }
            if (!result.destination) {
                return;
            }
            const from = result.source.index;
            const to = result.destination.index;
            if (from === to) {
                return;
            }
            void state.applyDetailSecondMenuDragReorder(from, to);
        },
        [state],
    );

    /** 子类别仅在编辑态且门店允许优先级排序时挂载拖拽能力 */
    const secondMenuReorderEnabled =
        state.isDetailHeaderEditMode && state.isDetailSecondMenuPrioritySortEnabled;

    /**
     * 菜品表行拖拽配置：须放在任意提前 `return` 之前，否则 loading/骨架 与主界面 hooks 数量不一致。
     * 启用条件：编辑模式 +（本地须 setting 22 为优先级排序；云端始终可排）+ 无搜索/筛选。
     */
    const dishTableRowReorderEnabled =
        state.isDetailHeaderEditMode &&
        state.isDetailDishPrioritySortEnabled &&
        !state.dishSearchKeyword.trim() &&
        !state.hasActiveDishFilters();
    const dishTableRowReorder = useMemo<VirtualizedMuiTableRowReorder | undefined>(() => {
        if (!dishTableRowReorderEnabled) {
            return undefined;
        }
        return {
            enabled: true,
            onReorder: (from, to) => state.applyDetailDishDragReorderInSelectedSlu(from, to),
            getDragHandleAriaLabel: () => i18n.t('menu_library_detail_drag_sort_handle'),
        };
    }, [dishTableRowReorderEnabled, state]);

    const detailSelectedItemCount = useMemo(() => detailDishSelectedKeys.length, [detailDishSelectedKeys]);
    const detailMenuHasDishSelection = detailSelectedItemCount > 0;

    const fm = state.detailFirstMenu;
    /**
     * 分享弹窗子标题：名称按 `menuDisplayLang` 解析 i18n（与菜品列表一致），并拼接 `last_updated_time`（`DD/MM/YYYY`），与列表卡片日期格式一致；逻辑见 `state.getShareMenuDialogSubtitle`。
     */
    const shareMenuDialogSubtitle = state.getShareMenuDialogSubtitle();
    /** 账号是否具备菜单商店上传权限（权限组 `auth_shell_2 === 1`） */
    const canSyncMenuToMenuStore = Number(accountState.userInfo?.auth_shell_2) === 1;
    /** 顶栏菜单名：对接 `main_group_name_i18n`，随语言按钮切换。不用 useMemo，避免 observer + useMemo 对 observable 订阅不完整导致切换语言不刷新。 */
    const detailHeaderLang = state.detailHeaderMenuNameDisplayLang;
    let headerMenuNameDisplay = '—';
    if (fm) {
        const fromSlot = getMainGroupNameForDisplay({
            primary: fm.main_group_name ?? '',
            i18nJson: fm.main_group_name_i18n ?? '',
            lang: detailHeaderLang,
        }).trim();
        headerMenuNameDisplay = fromSlot !== '' ? fromSlot : fm.main_group_name?.trim() || '—';
    }

    if (!state.detailLoading && state.isMenuDetailOpen && !fm) {
        return (
            <Box className="menu-library-menu-detail">
                <Typography variant="body1">{i18n.t('global_text_operate_failure')}</Typography>
                <Button color="primary" onClick={() => state.closeMenuDetail()}>
                    {i18n.t('menu_library_detail_back')}
                </Button>
            </Box>
        );
    }
    if (state.detailLoading && state.isMenuDetailOpen && !state.detailFirstLoadCompleted) {
        return <MenuLibraryMenuDetailSkeleton onBack={() => state.closeMenuDetail()} />;
    }
    const rawSecondList = fm?.menu_item_slu;
    const secondList = Array.isArray(rawSecondList) ? rawSecondList : [];
    /** 详情已完成加载且当前一级菜单没有任何子类别。 */
    const hasNoSecondMenus =
        !state.detailLoading &&
        state.detailFirstMenu != null &&
        Array.isArray(rawSecondList) &&
        secondList.length === 0;
    const prices = state.getPriceLineForWeek();
    /** 下拉项：占位 + 额外费用列表（与制作器 extraMenuDataOptions 同源） */
    const priceEditMenuItems = [
        { value: '', label: i18n.t('menu_library_detail_price_select_placeholder') },
        ...state.getExtraMenuDataOptions(),
    ];
    const dishes = state.getFilteredDishes();
    const currentSecondForBatch = state.getCurrentSecondMenu();
    /** 删除确认：菜单名按 `menuDisplayLang`（与顶栏展示语言一致） */
    const deleteConfirmMenuName =
        fm != null
            ? getMainGroupNameForDisplay({
                  primary: fm.main_group_name ?? '',
                  i18nJson: fm.main_group_name_i18n ?? '',
                  lang: state.menuDisplayLang,
              }).trim() ||
              fm.main_group_name?.trim() ||
              ''
            : '';
    /** 删除确认：当前小类名按同一展示语言 */
    const deleteConfirmSubcategoryName =
        currentSecondForBatch != null
            ? getMainGroupNameForDisplay({
                  primary: currentSecondForBatch.dmi_slu_name ?? '',
                  i18nJson: currentSecondForBatch.dmi_slu_name_i18n ?? '',
                  lang: state.menuDisplayLang,
              }).trim() ||
              currentSecondForBatch.dmi_slu_name?.trim() ||
              ''
            : '';
    /** 空菜单允许删除一级菜单；空小类允许删除当前小类。 */
    const hasRestaurantId = Boolean(state.resolveRbpStringIdForApi().trim());
    const canDeleteMenu =
        hasRestaurantId &&
        canDeleteEmptyMenu(fm) &&
        (state.isCloudMenuDetail ? Boolean(state.detailCloudMenuId) : fm?.main_group_id != null);
    const canDeleteSubcategory =
        hasRestaurantId &&
        !canDeleteMenu &&
        canDeleteEmptySubcategory(currentSecondForBatch) &&
        (state.isCloudMenuDetail
            ? state.getCloudSluIdFromSecondMenu(currentSecondForBatch) != null
            : currentSecondForBatch?.dmi_slu_id != null);
    let detailDeleteType: 'menu' | 'subcategory' | null = null;
    if (canDeleteMenu) {
        detailDeleteType = 'menu';
    } else if (canDeleteSubcategory) {
        detailDeleteType = 'subcategory';
    }
    /** 当前选中小类已加载完成且没有任何菜品。 */
    const hasNoDishes =
        !state.detailLoading &&
        state.detailFirstMenu != null &&
        secondList.length > 0 &&
        currentSecondForBatch != null &&
        canDeleteEmptySubcategory(currentSecondForBatch);
    /** 菜品表列（虚拟表格；展示语言等变化时由 observer 重渲染） */
    const dishTableColumns: VirtualizedMuiTableColumn<MenuDish>[] = [
        {
            id: 'dish_name',
            header: i18n.t('business_analysis_page_dishes_name_one'),
            align: 'left',
            renderCell: (row) => state.getDishDisplayName(row),
            width: '200px',
        },
        {
            id: 'dine_in',
            header: i18n.t('menu_library_detail_col_dine_in'),
            renderCell: (row) => (
                <>
                    {state.getModuleCurrencyName()} {row.price_1 ?? '—'}
                </>
            ),
        },
        {
            id: 'takeout',
            header: i18n.t('menu_library_detail_col_takeout'),
            renderCell: (row) => (
                <>
                    {state.getModuleCurrencyName()} {row.takeout_price_1 ?? '—'}
                </>
            ),
        },
        {
            id: 'print',
            header: i18n.t('menu_library_detail_col_print'),
            renderCell: (row) => state.getPrintClassLabel(row.print_class),
        },
        {
            id: 'nlu',
            header: i18n.t('menu_library_detail_col_nlu'),
            // 助记码为 null / undefined / 空字符串时均展示为空，不用「—」占位
            renderCell: (row) => row.nlu ?? '',
        },
        {
            id: 'limit',
            header: i18n.t('menu_library_detail_col_limit'),
            renderCell: (row) => row.rule_lnr_max_num ?? '—',
        },
        {
            id: 'type',
            header: i18n.t('menu_library_detail_col_type'),
            renderCell: (row) => state.getDishTypeLabel(row.item_type),
        },
        {
            id: 'detail',
            align: 'right',
            header: i18n.t('menu_library_detail_col_detail'),
            renderCell: (row) => (
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => state.openDishDetailDialog(row)}
                    sx={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
                >
                    {i18n.t('menu_library_detail_view_detail')}
                </Button>
            ),
        },
    ];

    const weekItems = MENU_LIBRARY_WEEK_VALUES.map((w) => ({
        value: String(w),
        label: i18n.t(weekValueToLabelKey(w)),
    }));

    return (
        <Box className="menu-library-menu-detail">
            <Paper elevation={0} className="menu-library-menu-detail__header-card">
                <Box className="menu-library-menu-detail__header-grid">
                    <Box className="menu-library-menu-detail__thumb-cell">
                        <RestaurantStaticImageUpload
                            stringId={state.resolveRbpStringIdForApi()}
                            kind="menu-image"
                            uploadEntityId={fm?.main_group_id ?? null}
                            imageRef={fm?.menu_image}
                            displayUrl={state.getFirstMenuAvatarUrl()}
                            onCommittedFileName={() => state.patchDetailFirstMenuImage()}
                            onRemoved={() => state.patchDetailFirstMenuImage()}
                            deferStaticUpload={state.isCloudMenuDetail}
                            onStagedStaticFile={(file) => state.uploadCloudDetailMenuImage(file)}
                            onDeferredStaticRemove={() => state.removeCloudDetailMenuImage()}
                            disabled={state.detailLoading}
                            avatarSize={128}
                            sx={{ position: 'absolute', inset: 0, minHeight: 128 }}
                        />
                    </Box>

                    <Box className="menu-library-menu-detail__header-row1">
                        <Box className="menu-library-menu-detail__header-row1-cluster">
                            {state.isDetailHeaderEditMode ? (
                                <MenuLibraryMenuNameEditor
                                    value={state.editMenuNameDraft}
                                    onChange={(e) => state.setEditMenuNameDraft(e.target.value)}
                                />
                            ) : (
                                <Box className="menu-library-menu-detail__price-pill menu-library-menu-detail__menu-name-field">
                                    <MenuLibraryMenuNameIcon className="menu-library-menu-detail__title-pill-icon" aria-hidden />
                                    <Typography
                                        variant="body1"
                                        className="menu-library-menu-detail__price-pill-text menu-library-menu-detail__menu-name-text"
                                        noWrap
                                    >
                                        {headerMenuNameDisplay}
                                    </Typography>
                                </Box>
                            )}
                            {state.isDetailHeaderEditMode ? (
                                <Box className="menu-library-menu-detail__price-pill menu-library-menu-detail__max-limit-field menu-library-menu-detail__max-limit-field--editing">
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        className="menu-library-menu-detail__max-limit-label"
                                    >
                                        {i18n.t('menu_library_detail_category_limit_label')}
                                    </Typography>
                                    <CommonInput
                                        inputType="INPUT"
                                        className="menu-library-menu-detail__max-limit-input"
                                        type="number"
                                        inputProps={{ min: 1, step: 1 }}
                                        value={state.detailHeaderMaxLimitDraft}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            state.setDetailHeaderMaxLimitDraft(e.target.value)
                                        }
                                        size="small"
                                        InputLabelProps={{ className: 'menu-library-menu-detail__week-label-hidden' }}
                                        label=""
                                    />
                                </Box>
                            ) : (
                                <Box className="menu-library-menu-detail__price-pill menu-library-menu-detail__max-limit-field">
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        className="menu-library-menu-detail__max-limit-label"
                                    >
                                        {i18n.t('menu_library_detail_category_limit_label')}
                                    </Typography>
                                    <Typography variant="body1" className="menu-library-menu-detail__max-limit-value">
                                        {fm?.max_limit != null && Number.isFinite(Number(fm.max_limit))
                                            ? String(fm.max_limit)
                                            : '—'}
                                    </Typography>
                                </Box>
                            )}
                            <MenuLanguageMenuButton
                                state={state}
                                value={state.detailHeaderMenuNameDisplayLang}
                                onChange={(lang) => state.setDetailHeaderMenuNameDisplayLang(lang)}
                            />
                            {state.isDetailHeaderEditMode ? (
                                <Tooltip title={i18n.t('menu_library_detail_cancel_edit')}>
                                    <Paper
                                        variant="outlined"
                                        component="button"
                                        type="button"
                                        className="menu-library-menu-detail__header-icon-pill menu-library-menu-detail__price-pill--clickable"
                                        onClick={() => state.cancelDetailHeaderEdit()}
                                        aria-label={i18n.t('menu_library_detail_cancel_edit')}
                                    >
                                        <EditOffRoundedIcon
                                            className="menu-library-menu-detail__header-icon-pill-icon"
                                            aria-hidden
                                        />
                                    </Paper>
                                </Tooltip>
                            ) : (
                                <Tooltip title={i18n.t('menu_library_detail_edit_menu')}>
                                    <Paper
                                        variant="outlined"
                                        component="button"
                                        type="button"
                                        className="menu-library-menu-detail__header-icon-pill menu-library-menu-detail__price-pill--clickable"
                                        onClick={() => state.startDetailHeaderEdit()}
                                        aria-label={i18n.t('menu_library_detail_edit_menu')}
                                    >
                                        <EditIcon className="menu-library-menu-detail__header-icon-pill-icon" aria-hidden />
                                    </Paper>
                                </Tooltip>
                            )}
                            {!state.isDetailHeaderEditMode && (
                                <Tooltip title={i18n.t('menu_library_detail_share_menu')}>
                                    <Paper
                                        variant="outlined"
                                        component="button"
                                        type="button"
                                        className="menu-library-menu-detail__header-icon-pill menu-library-menu-detail__price-pill--clickable"
                                        onClick={() => state.handleShareMenu()}
                                        aria-label={i18n.t('menu_library_detail_share_menu')}
                                    >
                                        <ShareIcon className="menu-library-menu-detail__header-icon-pill-icon" aria-hidden />
                                    </Paper>
                                </Tooltip>
                            )}
                            {state.isDetailHeaderEditMode && (
                                <Tooltip title={i18n.t('menu_library_detail_save_changes')}>
                                    <Paper
                                        variant="outlined"
                                        component="button"
                                        type="button"
                                        disabled={state.isDetailHeaderSaveLoading}
                                        className="menu-library-menu-detail__header-icon-pill menu-library-menu-detail__price-pill--clickable"
                                        onClick={() => void state.saveDetailHeaderEdit()}
                                        aria-label={i18n.t('menu_library_detail_save_changes')}
                                    >
                                        <SaveIcon className="menu-library-menu-detail__header-icon-pill-icon" aria-hidden />
                                    </Paper>
                                </Tooltip>
                            )}
                        </Box>
                        <Tooltip title={i18n.t('menu_library_detail_back')}>
                            <IconButton
                                size="large"
                                onClick={() => state.closeMenuDetail()}
                                aria-label={i18n.t('menu_library_detail_back')}
                                className="menu-library-menu-detail__header-nav-right"
                            >
                                <TablerChevronDownIcon className="menu-library-menu-detail__header-chevron-right" />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box className="menu-library-menu-detail__header-row2">
                        {/**
                         * 绑定费用（额外费用）：仅本地详情展示。
                         * 云端详情显式关闭（领域 §4.6）：不展示按周选择与三档药丸。
                         */}
                        {!state.isCloudMenuDetail ? (
                            <>
                                {/** 新菜单价格方案（`set_item_main === '1'`）按周编辑；旧方案 `'0'` 不展示周期选择器，与 first-menu-detail 一致 */}
                                {fm?.set_item_main === '1' ? (
                                    <Box className="menu-library-menu-detail__week-field">
                                        <CommonInput
                                            inputType="SELECT_PILL"
                                            items={weekItems}
                                            value={state.selectedWeek}
                                            onChange={(e: any) => {
                                                state.setSelectedWeek(Number(e.target.value));
                                            }}
                                            InputLabelProps={{ className: 'menu-library-menu-detail__week-label-hidden' }}
                                            size="small"
                                            sx={{
                                                width: weekPillSelectWidth,
                                                minWidth: weekPillSelectWidth,
                                                maxWidth: weekPillSelectWidth,
                                                borderRadius: '8px',
                                                border: '1px solid var(--stroke-primary-100)',
                                                background: 'var(--fill-neutral-100)',
                                                boxShadow: '0 4px 4px 0 var(--shadow-25)',
                                                height: '48px',
                                                boxSizing: 'border-box',
                                                alignItems: 'center',
                                                '& .MuiFormControl-root': {
                                                    minWidth: weekPillSelectWidth,
                                                    height: '100%',
                                                },
                                                '& .MuiInputBase-root': {
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                                '& .MuiSelect-select': {
                                                    py: 0,
                                                    minHeight: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    boxSizing: 'border-box',
                                                },
                                            }}
                                        />
                                    </Box>
                                ) : null}
                                {state.isDetailHeaderEditMode ? (
                                    <>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            gap={1}
                                            className="menu-library-menu-detail__price-pills menu-library-menu-detail__price-pills--edit"
                                        >
                                            {(
                                                [
                                                    {
                                                        Icon: MenuLibraryPriceAdultFaceIcon,
                                                        labelKey: 'menu_library_detail_price_adult',
                                                        fieldKey: 'adult' as const,
                                                        extraField: PRICE_KEY_TO_EXTRA.adult,
                                                    },
                                                    {
                                                        Icon: ChildCareIcon,
                                                        labelKey: 'menu_library_detail_price_child',
                                                        fieldKey: 'child' as const,
                                                        extraField: PRICE_KEY_TO_EXTRA.child,
                                                    },
                                                    {
                                                        Icon: FaceSmileIcon,
                                                        labelKey: 'menu_library_detail_price_other',
                                                        fieldKey: 'other' as const,
                                                        extraField: PRICE_KEY_TO_EXTRA.other,
                                                    },
                                                ] as const
                                            ).map(({ Icon, labelKey, fieldKey, extraField }) => (
                                                <Paper
                                                    key={fieldKey}
                                                    variant="outlined"
                                                    component="button"
                                                    type="button"
                                                    className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--stacked menu-library-menu-detail__price-pill--clickable menu-library-menu-detail__price-pill--edit-trigger"
                                                    onClick={(e) => {
                                                        setPriceEditMenuField(fieldKey);
                                                        setPriceEditMenuAnchor(e.currentTarget);
                                                    }}
                                                    aria-haspopup="listbox"
                                                    aria-expanded={
                                                        Boolean(priceEditMenuAnchor) &&
                                                        priceEditMenuField === fieldKey
                                                    }
                                                >
                                                    <Icon
                                                        className="menu-library-menu-detail__price-pill-icon"
                                                        aria-hidden
                                                    />
                                                    <Box className="menu-library-menu-detail__price-pill-stack">
                                                        <Typography
                                                            variant="body2"
                                                            component="span"
                                                            className="menu-library-menu-detail__price-pill-stack-label"
                                                        >
                                                            {i18n.t(labelKey)}
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            component="span"
                                                            className="menu-library-menu-detail__price-pill-stack-value"
                                                            noWrap
                                                        >
                                                            {(() => {
                                                                const sum =
                                                                    state.getHeaderEditPillSumDisplay(extraField);
                                                                return sum === '—' || sum.trim() === ''
                                                                    ? '—'
                                                                    : `${state.getModuleCurrencyName()} ${sum}`;
                                                            })()}
                                                        </Typography>
                                                    </Box>
                                                    <ArrowDropdownIcon
                                                        className="menu-library-menu-detail__price-pill-dropdown-arrow"
                                                        aria-hidden
                                                    />
                                                </Paper>
                                            ))}
                                        </Stack>
                                        <Menu
                                            anchorEl={priceEditMenuAnchor}
                                            open={Boolean(priceEditMenuAnchor && priceEditMenuField)}
                                            onClose={() => {
                                                setPriceEditMenuAnchor(null);
                                                setPriceEditMenuField(null);
                                            }}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                            PaperProps={{
                                                className: 'menu-library-menu-detail__price-edit-menu-paper',
                                            }}
                                            /** `Menu` 内部已包一层 `MenuList`；用 `ListItemButton` 替代 `MenuItem`，避免点击即关闭，便于多选 */
                                            MenuListProps={{ autoFocusItem: false, dense: true }}
                                        >
                                            {priceEditMenuItems.map((item) => {
                                                const extra = priceEditMenuField
                                                    ? PRICE_KEY_TO_EXTRA[priceEditMenuField]
                                                    : null;
                                                const ids = extra
                                                    ? state.getHeaderExtraIdsForField(extra)
                                                    : [];
                                                const selected =
                                                    !!extra &&
                                                    (item.value === ''
                                                        ? ids.length === 0
                                                        : ids.includes(item.value));
                                                return (
                                                    <ListItemButton
                                                        key={item.value === '' ? '__empty' : item.value}
                                                        selected={selected}
                                                        role="menuitem"
                                                        onClick={() => {
                                                            if (!extra) {
                                                                return;
                                                            }
                                                            if (item.value === '') {
                                                                state.clearHeaderExtraField(extra);
                                                            } else {
                                                                state.toggleHeaderExtraIdInMenu(extra, item.value);
                                                            }
                                                        }}
                                                    >
                                                        <ListItemText primary={item.label} />
                                                    </ListItemButton>
                                                );
                                            })}
                                        </Menu>
                                    </>
                                ) : (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        className="menu-library-menu-detail__price-pills"
                                    >
                                        <Paper
                                            variant="outlined"
                                            className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--stacked"
                                        >
                                            <MenuLibraryPriceAdultFaceIcon
                                                className="menu-library-menu-detail__price-pill-icon"
                                                aria-hidden
                                            />
                                            <Box className="menu-library-menu-detail__price-pill-stack">
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-label"
                                                >
                                                    {i18n.t('menu_library_detail_price_adult')}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-value"
                                                >
                                                    {state.getModuleCurrencyName()} {prices.adult}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                        <Paper
                                            variant="outlined"
                                            className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--stacked"
                                        >
                                            <ChildCareIcon
                                                className="menu-library-menu-detail__price-pill-icon"
                                                aria-hidden
                                            />
                                            <Box className="menu-library-menu-detail__price-pill-stack">
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-label"
                                                >
                                                    {i18n.t('menu_library_detail_price_child')}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-value"
                                                >
                                                    {state.getModuleCurrencyName()} {prices.child}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                        <Paper
                                            variant="outlined"
                                            className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--stacked"
                                        >
                                            <FaceSmileIcon
                                                className="menu-library-menu-detail__price-pill-icon"
                                                aria-hidden
                                            />
                                            <Box className="menu-library-menu-detail__price-pill-stack">
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-label"
                                                >
                                                    {i18n.t('menu_library_detail_price_other')}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    className="menu-library-menu-detail__price-pill-stack-value"
                                                >
                                                    {state.getModuleCurrencyName()} {prices.other}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Stack>
                                )}
                            </>
                        ) : null}
                        <Stack direction="row" spacing={2} alignItems="center" className="menu-library-menu-detail__batch">
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                {i18n.t('menu_library_detail_batch_round')}
                            </Typography>
                            <IOSSwitch
                                className="menu-library-menu-detail__batch-ios-switch"
                                loading={state.isBatchRoundToggleLoading}
                                checked={state.getBatchRoundAllUnrestricted()}
                                onChange={() => {
                                    void state.toggleBatchRoundLimit();
                                }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" className="menu-library-menu-detail__util-icons">
                            <TooltipIconButton
                                titleKey="menu_library_detail_filter"
                                onClick={() => state.openDishFilterDialog()}
                                active={state.isDishFilterDialogOpen || state.hasActiveDishFilters()}
                            >
                                <Box component="span" className="menu-library-menu-detail__filter-icon-wrap">
                                    <FilterIcon className="menu-library-menu-detail__icon menu-library-menu-detail__icon--primary" />
                                    {state.hasActiveDishFilters() ? (
                                        <span className="menu-library-menu-detail__filter-active-dot" aria-hidden />
                                    ) : null}
                                </Box>
                            </TooltipIconButton>
                            <TooltipIconButton
                                titleKey="menu_library_detail_search"
                                onClick={() => state.toggleDishSearchDialog()}
                                active={
                                    state.isDishSearchDialogOpen || state.dishSearchKeyword.trim().length > 0
                                }
                            >
                                <Box component="span" className="menu-library-menu-detail__search-icon-wrap">
                                    <SearchIcon className="menu-library-menu-detail__icon menu-library-menu-detail__icon--primary" />
                                    {state.dishSearchKeyword.trim().length > 0 ? (
                                        <span className="menu-library-menu-detail__search-active-dot" aria-hidden />
                                    ) : null}
                                </Box>
                            </TooltipIconButton>
                        </Stack>
                    </Box>
                </Box>

            </Paper>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} className="menu-library-menu-detail__body">
                {/* 左侧子类列表 */}
                <Paper elevation={0} className="menu-library-menu-detail__left">
                    <Stack direction="row" spacing={3} alignItems="center" className="menu-library-menu-detail__left-title">
                        <MenuLibraryMenuNameIcon className="menu-library-menu-detail__inline-icon" />
                        <Typography className="menu-library-menu-detail__left-title-text">{i18n.t('menu_library_detail_subcategory_title')}</Typography>
                    </Stack>
                    <Box className="menu-library-menu-detail__left-list-scroll">
                        <List dense disablePadding component="div">
                            {state.isDetailHeaderEditMode ? (
                                <ListItemButton
                                    className="menu-library-menu-detail__slu-item menu-library-menu-detail__slu-item--add"
                                    aria-label={i18n.t('menu_library_detail_add_subcategory')}
                                    onClick={() => state.openDetailAddSubcategoryDialog()}
                                >
                                    <Box className="menu-library-menu-detail__slu-item-inner">
                                        <Typography className="menu-library-menu-detail__slu-item-label" component="span" variant="body1">
                                            {i18n.t('menu_library_detail_add_subcategory')}
                                        </Typography>
                                        <AddRoundedIcon className="menu-library-menu-detail__slu-item-icon" aria-hidden />
                                    </Box>
                                </ListItemButton>
                            ) : null}
                            {secondMenuReorderEnabled ? (
                                <MenuLibraryDndContext onDragEnd={onDetailSecondSluDragEnd}>
                                    <MenuLibraryDroppable droppableId="menu-library-detail-second-slu">
                                        {(dropProvided) => (
                                            <div
                                                ref={dropProvided.innerRef}
                                                {...dropProvided.droppableProps}
                                                style={{ minHeight: secondList.length ? undefined : 8 }}
                                            >
                                                {secondList.map((slu, index) => {
                                                    const sluKey = getMenuLibraryDetailSecondSluKey(
                                                        slu,
                                                        index,
                                                        state.isCloudMenuDetail,
                                                    );
                                                    const selected = state.isDetailSecondSluSelected(slu);
                                                    return (
                                                        <MenuLibraryDraggable
                                                            key={String(sluKey)}
                                                            draggableId={`detail-slu-${sluKey}`}
                                                            index={index}
                                                        >
                                                            {(dragProvided, snapshot) => (
                                                                <ListItemButton
                                                                    component="div"
                                                                    ref={dragProvided.innerRef}
                                                                    {...dragProvided.draggableProps}
                                                                    selected={selected}
                                                                    onClick={() => state.setSelectedDetailSecondSlu(slu)}
                                                                    className="menu-library-menu-detail__slu-item"
                                                                    sx={{
                                                                        ...(snapshot.isDragging
                                                                            ? {
                                                                                boxShadow: 2,
                                                                                opacity: 0.95,
                                                                            }
                                                                            : {}),
                                                                    }}
                                                                >
                                                                    <Box className="menu-library-menu-detail__slu-item-inner menu-library-menu-detail__slu-item-inner--with-edit">

                                                                        <Typography
                                                                            component="span"
                                                                            variant="body1"
                                                                            className="menu-library-menu-detail__slu-item-label"
                                                                        >
                                                                            {slu.dmi_slu_name || '—'}
                                                                        </Typography>
                                                                        <Box
                                                                            {...dragProvided.dragHandleProps}
                                                                            aria-label={i18n.t(
                                                                                'menu_library_detail_drag_sort_handle',
                                                                            )}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                flexShrink: 0,
                                                                                cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                                                                mr: 0.5,
                                                                                color: selected ? 'var(--icon-neutral-100)' : 'var(--text-primary-300)',
                                                                                p: 1,
                                                                                'svg': {
                                                                                    fontSize: '24px',
                                                                                },
                                                                            }}
                                                                            className="menu-library-menu-detail__slu-item-drag-handle"
                                                                        >
                                                                            <DragIndicatorIcon fontSize="small" />
                                                                        </Box>
                                                                        <Tooltip title={i18n.t('menu_library_detail_edit_subcategory')}>
                                                                            <IconButton
                                                                                type="button"
                                                                                size="small"
                                                                                className="menu-library-menu-detail__slu-item-edit"
                                                                                aria-label={i18n.t(
                                                                                    'menu_library_detail_edit_subcategory',
                                                                                )}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    e.preventDefault();
                                                                                    if (state.isCloudMenuDetail) {
                                                                                        const cloudSluId =
                                                                                            state.getCloudSluIdFromSecondMenu(slu);
                                                                                        if (cloudSluId) {
                                                                                            state.openDetailEditSubcategoryDialogByCloudSluId(
                                                                                                cloudSluId,
                                                                                            );
                                                                                        }
                                                                                    } else if (slu.dmi_slu_id != null) {
                                                                                        state.openDetailEditSubcategoryDialog(
                                                                                            slu.dmi_slu_id as number,
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <EditIcon
                                                                                    className="menu-library-menu-detail__slu-item-edit-icon"
                                                                                    aria-hidden
                                                                                />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </ListItemButton>
                                                            )}
                                                        </MenuLibraryDraggable>
                                                    );
                                                })}
                                                {dropProvided.placeholder as unknown as React.ReactNode}
                                            </div>
                                        )}
                                    </MenuLibraryDroppable>
                                </MenuLibraryDndContext>
                            ) : (
                                secondList.map((slu, index) => {
                                    const sluKey = getMenuLibraryDetailSecondSluKey(
                                        slu,
                                        index,
                                        state.isCloudMenuDetail,
                                    );
                                    const selected = state.isDetailSecondSluSelected(slu);
                                    return (
                                        <ListItemButton
                                            key={String(sluKey)}
                                            selected={selected}
                                            onClick={() => state.setSelectedDetailSecondSlu(slu)}
                                            className="menu-library-menu-detail__slu-item"
                                        >
                                            <Box className="menu-library-menu-detail__slu-item-inner menu-library-menu-detail__slu-item-inner--with-edit">
                                                <Typography
                                                    component="span"
                                                    variant="body1"
                                                    className="menu-library-menu-detail__slu-item-label"
                                                >
                                                    {slu.dmi_slu_name || '—'}
                                                </Typography>
                                                {state.isDetailHeaderEditMode ? (
                                                    <Tooltip title={i18n.t('menu_library_detail_edit_subcategory')}>
                                                        <IconButton
                                                            type="button"
                                                            size="small"
                                                            className="menu-library-menu-detail__slu-item-edit"
                                                            aria-label={i18n.t(
                                                                'menu_library_detail_edit_subcategory',
                                                            )}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                if (state.isCloudMenuDetail) {
                                                                    const cloudSluId =
                                                                        state.getCloudSluIdFromSecondMenu(slu);
                                                                    if (cloudSluId) {
                                                                        state.openDetailEditSubcategoryDialogByCloudSluId(
                                                                            cloudSluId,
                                                                        );
                                                                    }
                                                                } else if (slu.dmi_slu_id != null) {
                                                                    state.openDetailEditSubcategoryDialog(
                                                                        slu.dmi_slu_id as number,
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon
                                                                className="menu-library-menu-detail__slu-item-edit-icon"
                                                                aria-hidden
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                            </Box>
                                        </ListItemButton>
                                    );
                                })
                            )}
                        </List>
                    </Box>
                </Paper>

                {/* 右侧菜品表 */}
                <Paper elevation={0} className="menu-library-menu-detail__right">
                    {hasNoSecondMenus ? (
                        <MenuLibraryDetailEmptySubcategoryState
                            canDeleteMenu={detailDeleteType === 'menu'}
                            onDeleteMenu={() => state.openDetailDeleteConfirm('menu')}
                            onAddSubcategory={() => state.openDetailAddSubcategoryDialog()}
                        />
                    ) : (
                        <Box className="menu-library-menu-detail__dish-table-stack">
                            <Box className="menu-library-menu-detail__dish-table-host">
                                <VirtualizedMuiTable<MenuDish>
                                    columns={dishTableColumns}
                                    rows={dishes}
                                    loading={state.detailLoading}
                                    loadingText={i18n.t('general_text_loading')}
                                    emptyText={i18n.t('general_text_no_or_more_data')}
                                    emptyContent={
                                        hasNoDishes ? (
                                            <MenuLibraryDetailEmptyDishState
                                                subcategoryName={currentSecondForBatch?.dmi_slu_name || '—'}
                                                isCloudMenuDetail={state.isCloudMenuDetail}
                                                canDeleteSubcategory
                                                showLoadCloudDish={!state.isCloudMenuDetail}
                                                deleteSubmitting={state.detailDeleteSubmitting}
                                                onDeleteSubcategory={() =>
                                                    state.openDetailDeleteConfirm('subcategory')
                                                }
                                                onLoadCloudDish={() => {
                                                    void state.openLoadCloudDishDialog();
                                                }}
                                                onAddDish={() => state.openMenuLibraryDetailAddDishDialog()}
                                            />
                                        ) : undefined
                                    }
                                    getRowKey={(row, index) =>
                                        getMenuLibraryDetailDishRowKey(row, index, state.isCloudMenuDetail)
                                    }
                                    height="100%"
                                    rowReorder={dishTableRowReorder}
                                    checkboxSelection={{
                                        selectedKeys: detailDishSelectedKeys,
                                        onChange: setDetailDishSelectedKeys,
                                        isRowSelectable: (row) =>
                                            isMenuLibraryDetailDishRowSelectable(row, state.isCloudMenuDetail),
                                    }}
                                />
                            </Box>
                            {!hasNoDishes ? (
                                <Box className="menu-library-menu-detail__dish-add-row">
                            {!canDeleteMenu && detailMenuHasDishSelection ? (
                                <Box className="menu-library-create-dialog menu-library-menu-detail__dish-footer-host">
                                    <Paper
                                        elevation={0}
                                        variant="outlined"
                                        className="menu-library-create-dialog__dish-batch-bar menu-library-menu-detail__dish-batch-bar"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <Button
                                            type="button"
                                            variant="text"
                                            className="menu-library-create-dialog__dish-batch-btn menu-library-create-dialog__dish-batch-btn--settings menu-library-menu-detail__dish-batch-btn menu-library-menu-detail__dish-batch-btn--with-icon-sm"
                                            startIcon={
                                                <TuneRoundedIcon
                                                    className="menu-library-menu-detail__dish-batch-mui-icon"
                                                    aria-hidden
                                                />
                                            }
                                            onClick={() => {
                                                if (state.isCloudMenuDetail) {
                                                    const cloudIds = detailDishSelectedKeys
                                                        .filter((k): k is string => typeof k === 'string')
                                                        .map((k) => k.trim())
                                                        .filter(
                                                            (id) =>
                                                                id !== '' &&
                                                                !id.startsWith('cloud-dish-idx:'),
                                                        );
                                                    state.openMenuLibraryDetailCloudBatchDishSettingsDialog(
                                                        cloudIds,
                                                    );
                                                    return;
                                                }
                                                const itemIds = detailDishSelectedKeys.filter(
                                                    (k): k is number => typeof k === 'number',
                                                );
                                                state.openMenuLibraryDetailBatchDishSettingsDialog(itemIds);
                                            }}
                                        >
                                            {i18n.t('menu_library_create_dialog_batch_dish_settings')}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="text"
                                            className="menu-library-create-dialog__dish-batch-btn menu-library-menu-detail__dish-batch-btn"
                                            startIcon={
                                                <span
                                                    className="menu-library-menu-detail__dish-batch-transfer-wrap"
                                                    aria-hidden
                                                >
                                                    <GgArrowsExchangeIcon className="menu-library-menu-detail__dish-batch-svg-icon menu-library-create-dialog__dish-batch-exchange-icon" />
                                                </span>
                                            }
                                            onClick={() => openDetailBatchSubcategoryPicker('transfer')}
                                        >
                                            {i18n.t('menu_library_create_dialog_batch_transfer_dishes')}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="text"
                                            className="menu-library-create-dialog__dish-batch-btn menu-library-menu-detail__dish-batch-btn"
                                            startIcon={
                                                <CopyBoldIcon
                                                    className="menu-library-menu-detail__dish-batch-svg-icon"
                                                    aria-hidden
                                                />
                                            }
                                            onClick={() => openDetailBatchSubcategoryPicker('copy')}
                                        >
                                            {i18n.t('menu_library_create_dialog_batch_copy_dishes')}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="text"
                                            className="menu-library-create-dialog__dish-batch-btn menu-library-create-dialog__dish-batch-btn--danger menu-library-menu-detail__dish-batch-btn"
                                            startIcon={
                                                <BaselineDeleteForeverIcon
                                                    className="menu-library-menu-detail__dish-batch-svg-icon"
                                                    aria-hidden
                                                />
                                            }
                                            disabled={state.detailLoading}
                                            onClick={() => setDetailBatchDeleteConfirmOpen(true)}
                                        >
                                            {i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                        </Button>
                                    </Paper>
                                    <AlertDialog
                                        open={detailBatchDeleteConfirmOpen}
                                        variant="delete"
                                        size="sm"
                                        title={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                        bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                                        highlightText={i18n.t(
                                            'menu_library_create_dialog_batch_delete_confirm_highlight',
                                            { count: detailSelectedItemCount },
                                        )}
                                        confirmText={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                        onCancel={() => setDetailBatchDeleteConfirmOpen(false)}
                                        onConfirm={() => {
                                            setDetailBatchDeleteConfirmOpen(false);
                                            void handleDetailBatchDelete();
                                        }}
                                    />
                                    <MenuLibrarySubcategoryPickerDialog
                                        open={!state.isCloudMenuDetail && detailBatchPickerMode != null}
                                        mode={detailBatchPickerMode}
                                        onClose={closeDetailBatchSubcategoryPicker}
                                        menuData={state.courseGroupPickerMenusAll ?? []}
                                        loading={state.courseGroupPickerMenusLoading}
                                        excludeSluIds={
                                            currentSecondForBatch?.dmi_slu_id != null
                                                ? [currentSecondForBatch.dmi_slu_id]
                                                : []
                                        }
                                        confirmLoading={detailBatchPickerConfirmLoading}
                                        onConfirm={handleDetailBatchSubcategoryPickerConfirm}
                                    />
                                    <MenuLibraryCloudSubcategoryPickerDialog
                                        open={state.isCloudMenuDetail && detailBatchPickerMode != null}
                                        mode={detailBatchPickerMode}
                                        onClose={closeDetailBatchSubcategoryPicker}
                                        cloudMenus={state.cloudMenuList}
                                        loading={state.cloudMenuListLoading}
                                        excludeCloudSluIds={
                                            state.getCurrentCloudSluId() != null
                                                ? [state.getCurrentCloudSluId() as string]
                                                : []
                                        }
                                        confirmLoading={detailBatchPickerConfirmLoading}
                                        onConfirm={handleDetailCloudBatchSubcategoryPickerConfirm}
                                    />
                                </Box>
                            ) : !canDeleteMenu ? (
                                /** Figma 9752:39007：底部「加载云端菜品」在左、「新增菜品」在右，间距 16px；云端详情不展示加载入口 */
                                <Box className="menu-library-menu-detail__dish-add-actions">
                                    {!state.isCloudMenuDetail && (
                                        <Button
                                            type="button"
                                            variant="text"
                                            color="primary"
                                            className="menu-library-menu-detail__dish-add-btn menu-library-menu-detail__dish-add-btn--load-cloud"
                                            onClick={() => void state.openLoadCloudDishDialog()}
                                            startIcon={
                                                <CloudDownloadIcon
                                                    aria-hidden
                                                    className="menu-library-menu-detail__dish-add-btn-icon"
                                                />
                                            }
                                        >
                                            {i18n.t('menu_library_load_cloud_open')}
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        variant="text"
                                        color="primary"
                                        className="menu-library-menu-detail__dish-add-btn menu-library-menu-detail__dish-add-btn--new-dish"
                                        onClick={() => state.openMenuLibraryDetailAddDishDialog()}
                                        startIcon={
                                            <AddRoundedIcon
                                                aria-hidden
                                                className="menu-library-menu-detail__dish-add-btn-icon"
                                            />
                                        }
                                    >
                                        {i18n.t('menu_library_create_dialog_add_dish')}
                                    </Button>
                                </Box>
                                ) : null}
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </Paper>
            </Stack>
            {/* 菜品筛选弹窗 */}
            <MenuLibraryDishFilterDialog state={state} />
            {/* 菜品搜索弹窗 */}
            <MenuLibraryDishSearchDialog state={state} />
            {/* 空菜单 / 空小类删除二次确认 */}
            <AlertDialog
                open={state.detailDeleteConfirmType != null}
                variant="delete"
                size="sm"
                title={i18n.t('menu_library_detail_delete_confirm_title')}
                bodyPrompt={i18n.t('menu_library_detail_delete_confirm_prompt')}
                highlightText={buildMenuLibraryDetailDeleteHighlightText({
                    type: state.detailDeleteConfirmType,
                    menuName: deleteConfirmMenuName,
                    subcategoryName: deleteConfirmSubcategoryName,
                })}
                confirmText={i18n.t('menu_library_detail_delete_confirm_button')}
                confirmLoading={state.detailDeleteSubmitting}
                onCancel={() => state.closeDetailDeleteConfirm()}
                onConfirm={() => void state.confirmDetailDelete()}
            />
            {/* 新增子类弹窗 */}
            <MenuLibraryAddSubcategoryDialog state={state} />

            {/* 分享菜单弹窗 */}
            <ShareMenuDialog
                open={state.isShareMenuDialogOpen}
                onClose={() => state.closeShareMenuDialog()}
                menuName={shareMenuDialogSubtitle}
                storeOptions={shareStoreOptions}
                /** 仅本地详情排除当前店；云端列表已置顶「当前门店」故不传 */
                currentContextStoreId={
                    state.isCloudMenuDetail ? undefined : state.menuLibraryRestaurant?.string_id
                }
                onSyncToSelectedStores={(ids, options) => void state.shareSyncToSelectedStores(ids, options)}
                onSyncToMenuStore={() => void state.shareSyncToMenuStore()}
                menuStoreEntryLoading={state.shareMenuDialogMenuStoreOpeningLoading}
                selectedStoresSubmitting={state.shareMenuDialogSelectedStoresSubmitting}
                showMenuStoreSync={canSyncMenuToMenuStore}
            />
            <MenuLibraryShareStoreTargetDialog
                open={state.isShareStoreTargetDialogOpen}
                onClose={() => state.closeShareStoreTargetDialog()}
                menuName={shareMenuDialogSubtitle}
                loading={state.shareStoreTargetListLoading}
                submitting={state.shareStoreTargetSubmitting}
                uploadedMenus={state.getShareStoreUploadedMenuCandidatesForGrid()}
                loadingMore={state.shareStoreTargetMyMenusLoadingMore}
                onLoadMore={() => void state.loadShareStoreTargetUploadedMore()}
                selection={state.shareStoreTargetSelection}
                onSelectionChange={(v) => state.setShareStoreTargetSelection(v)}
                onSubmit={() => void state.submitShareStoreTargetChoice()}
            />
            <MenuLibraryShareSuccessDialog
                open={state.isShareSuccessDialogOpen}
                onClose={() => state.closeShareSuccessDialog()}
                menuName={state.shareSuccessDialogMenuName}
                lines={state.shareSuccessDialogLines}
                hadSuccessfulSync={state.shareSuccessHadSuccessfulSync}
            />
        </Box>
    );
});

/** 带 Tooltip 的图标按钮（筛选/搜索） */
function TooltipIconButton(props: {
    titleKey: string;
    onClick: () => void;
    active: boolean;
    children: React.ReactNode;
}) {
    const { titleKey, onClick, active, children } = props;
    return (
        <Tooltip title={i18n.t(titleKey)}>
            <IconButton
                size="large"
                onClick={onClick}
                aria-label={i18n.t(titleKey)}
                sx={{
                    width: '48px',
                    height: '48px',
                    padding: 0,
                }}
                className={
                    active
                        ? 'menu-library-menu-detail__icon-btn menu-library-menu-detail__icon-btn--active'
                        : 'menu-library-menu-detail__icon-btn'
                }
            >
                {children}
            </IconButton>
        </Tooltip>
    );
}

export default MenuLibraryMenuDetailComponent;
